import { useState } from "react";
import { toast } from "sonner";
import { Bot, Pause, Phone, Play, Square, OctagonX, Volume2, Sparkles, Megaphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScriptBuilder } from "@/components/ScriptBuilder";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, StatusBadge } from "@/components/StatusBits";
import { BriefingFields } from "@/components/BriefingFields";
import { CallBrief } from "@/components/dialer/CallBrief";
import { usePhone } from "@/context/PhoneContext";
import { useApp } from "@/context/AppContext";
import { api, errMsg, fmtDT } from "@/lib/api";

const Pick = ({ value, onChange, items, placeholder, testId }) => (
  <Select value={value || ""} onValueChange={onChange}>
    <SelectTrigger data-testid={testId} className="h-9 bg-white text-xs"><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent className="bg-white">
      {items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
    </SelectContent>
  </Select>
);

const SessionBox = ({ session, lastSession, cfg, queueIds, reload }) => {
  const [busy, setBusy] = useState(false);
  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const start = () => run(() => api.post("/sessions/start", {
    mode: cfg.mode, contact_ids: queueIds, from_number: cfg.fromNumber || null, script_id: cfg.source === "script" ? cfg.scriptId || null : null,
    voice_id: cfg.voiceId || null, briefing: cfg.briefing, frequency: cfg.frequency || {}, client_request_id: crypto.randomUUID(),
  }));
  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="session-box">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-slate-800">Queue session</span>
        {session ? <StatusBadge value={session.status} testId="session-status" /> : <StatusBadge value="stopped" testId="session-status">Not running</StatusBadge>}
      </div>
      {session && (
        <p className="text-xs text-slate-600" data-testid="session-waiting-reason">
          {{ ai: "AI", message: "Recorded message", agent: "Live agent" }[session.mode]} · {session.calls_placed} placed · {session.waiting_reason}
          {session.next_attempt_at && <> · next {fmtDT(session.next_attempt_at)}</>}
          {session.awaiting_disposition && <b className="block text-amber-700">Waiting for your disposition</b>}
        </p>
      )}
      {!session && lastSession?.waiting_reason && (
        <p className="text-xs text-amber-700" data-testid="session-last-result">Last run ({lastSession.calls_placed || 0} placed): {lastSession.waiting_reason}</p>
      )}
      <div className="flex flex-wrap gap-2">
        {!session && <Button size="sm" data-testid="session-start-btn" disabled={busy || !queueIds.length} onClick={start}><Play className="h-4 w-4 mr-1" />Start ({queueIds.length})</Button>}
        {session?.status === "active" && <Button size="sm" variant="outline" data-testid="session-pause-btn" disabled={busy} onClick={() => run(() => api.post(`/sessions/${session.id}/pause`))}><Pause className="h-4 w-4 mr-1" />Pause</Button>}
        {session?.status === "paused" && <Button size="sm" variant="outline" data-testid="session-resume-btn" disabled={busy} onClick={() => run(() => api.post(`/sessions/${session.id}/resume`))}><Play className="h-4 w-4 mr-1" />Resume</Button>}
        {session && <Button size="sm" variant="outline" data-testid="session-stop-btn" disabled={busy} onClick={() => run(() => api.post(`/sessions/${session.id}/stop`))}><Square className="h-4 w-4 mr-1" />Stop</Button>}
        <Button size="sm" data-testid="session-stop-all-btn" className="bg-rose-600 hover:bg-rose-700" disabled={busy} onClick={() => run(async () => {
          const { data } = await api.post("/sessions/stop-all");
          toast.success(`Stopped ${data.stopped} session(s)`);
        })}><OctagonX className="h-4 w-4 mr-1" />Stop all</Button>
      </div>
    </div>
  );
};

export const ControlsPanel = ({ cfg, setCfg, numbers, scripts, voices, contact, session, lastSession, queueIds, reload, onScriptSaved }) => {
  const { status } = useApp();
  const { startBrowserCall, active, deviceState } = usePhone();
  const [busy, setBusy] = useState(false);
  const [builder, setBuilder] = useState(false);
  const set = (k) => (v) => setCfg({ ...cfg, [k]: v });
  const setFreq = (k) => (e) => setCfg({ ...cfg, frequency: { ...(cfg.frequency || {}), [k]: e.target.value } });
  const twilioOk = status?.twilio?.configured;
  const aiOk = status?.elevenlabs?.configured && (cfg.mode === "message" || status?.ai?.configured);

  const preview = async () => {
    try {
      const { data } = await api.post("/voices/preview", { voice_id: cfg.voiceId });
      new Audio(data.audio).play();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const call = async (mode) => {
    setBusy(true);
    try {
      const { data } = await api.post("/calls/start", { contact_id: contact.id, mode, from_number: cfg.fromNumber || null, script_id: cfg.source === "script" ? cfg.scriptId || null : null, voice_id: cfg.voiceId || null, briefing: cfg.briefing });
      if (mode === "browser") await startBrowserCall(data);
      else toast.success(mode === "message" ? "Message call placed" : "AI call placed");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="lg:col-span-3 flex flex-col gap-4 bg-white border border-slate-200 rounded-lg p-4 overflow-y-auto scroll-thin min-h-0" data-testid="controls-panel">
      {!twilioOk && <p className="text-xs rounded-md border border-slate-200 bg-slate-50 p-2 text-slate-600" data-testid="calling-not-configured">Twilio: Not configured. Calling is blocked until an admin adds credentials in Settings → Integrations.</p>}
      <div className="grid grid-cols-2 gap-2">
        <Field label="Mode">
          <Pick testId="mode-select" value={cfg.mode} onChange={set("mode")} items={[["agent", "Live agent"], ["ai", "AI caller"], ["message", "Repeating message"]]} />
        </Field>
        <Field label="Outbound number">
          <Pick testId="from-number-select" value={cfg.fromNumber} onChange={set("fromNumber")} placeholder="First enabled" items={numbers.map((n) => [n.phone_number, n.phone_number])} />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1" data-testid="brief-source-toggle">
        {[["describe", "Describe this call"], ["script", "Use a saved script"]].map(([v, l]) => (
          <button key={v} type="button" data-testid={`brief-source-${v}`} onClick={() => setCfg({ ...cfg, source: v })}
            className={`rounded-md py-1.5 text-xs font-medium transition-colors duration-200 ${(cfg.source || "describe") === v ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>{l}</button>
        ))}
      </div>
      {cfg.source === "script" && <Field label="Script">
        <div className="flex gap-2">
          <div className="flex-1 min-w-0"><Pick testId="script-select" value={cfg.scriptId} onChange={set("scriptId")} placeholder="Choose a script" items={scripts.map((s) => [s.id, `${s.name}${s.kind === "message" ? " (message)" : ""}`])} /></div>
          <Button size="sm" variant="outline" className="h-9" data-testid="open-script-builder-btn" onClick={() => setBuilder(true)}><Sparkles className="h-4 w-4 mr-1" />Build</Button>
        </div>
      </Field>}
      <ScriptBuilder open={builder} onOpenChange={setBuilder} onSaved={(s) => {
        onScriptSaved?.(s);
        setCfg({ ...cfg, source: "script", scriptId: s.id, mode: s.kind === "message" ? "message" : "ai", frequency: s.frequency || cfg.frequency });
      }} />
      <Field label="AI voice">
        <div className="flex gap-2">
          <div className="flex-1"><Pick testId="voice-select" value={cfg.voiceId} onChange={set("voiceId")} placeholder={status?.elevenlabs?.configured ? "Choose a voice" : "ElevenLabs: Not configured"} items={voices.map((v) => [v.voice_id, v.name])} /></div>
          <Button size="icon" variant="outline" data-testid="voice-preview-btn" disabled={!cfg.voiceId} onClick={preview}><Volume2 className="h-4 w-4" /></Button>
        </div>
      </Field>
      <div className="grid grid-cols-2 gap-2">
        <Button data-testid="call-browser-btn" disabled={!contact || busy || !!active || deviceState !== "ready"} className="bg-emerald-600 hover:bg-emerald-700" onClick={() => call("browser")}>
          <Phone className="h-4 w-4 mr-1" /> Call now
        </Button>
        <Button data-testid="call-ai-btn" variant="outline" disabled={!contact || busy || !aiOk || !cfg.voiceId} onClick={() => call(cfg.mode === "message" ? "message" : "ai")}>
          {cfg.mode === "message" ? <Megaphone className="h-4 w-4 mr-1" /> : <Bot className="h-4 w-4 mr-1" />} {cfg.mode === "message" ? "Message call" : "AI call"}
        </Button>
      </div>
      <div data-testid="campaign-frequency">
        <p className="text-xs font-medium text-slate-600 mb-1">Repeat calling for this session (blank = defaults)</p>
        <div className="grid grid-cols-3 gap-2">
          <Input type="number" min="1" data-testid="freq-retry" className="h-8 text-xs" placeholder="Retry min" value={cfg.frequency?.retry_interval_minutes ?? ""} onChange={setFreq("retry_interval_minutes")} />
          <Input type="number" min="1" data-testid="freq-day" className="h-8 text-xs" placeholder="Per day" value={cfg.frequency?.max_attempts_per_day ?? ""} onChange={setFreq("max_attempts_per_day")} />
          <Input type="number" min="1" data-testid="freq-total" className="h-8 text-xs" placeholder="Total" value={cfg.frequency?.max_total_attempts ?? ""} onChange={setFreq("max_total_attempts")} />
        </div>
        <p className="text-[10px] text-slate-400 mt-0.5">Retry every (minutes) · max per day · max total</p>
      </div>
      {(cfg.source || "describe") === "describe" && cfg.mode !== "agent" && (
        <div className="rounded-lg border border-slate-200 p-3">
          <h3 className="text-sm font-semibold text-slate-800 mb-2">This call</h3>
          <CallBrief mode={cfg.mode} value={cfg.briefing} onChange={(b) => setCfg({ ...cfg, briefing: b })} />
        </div>
      )}
      <SessionBox session={session} lastSession={lastSession} cfg={cfg} queueIds={queueIds} reload={reload} />
      {cfg.source === "script" && cfg.mode !== "agent" && (
        <div>
          <h3 className="text-sm font-semibold text-slate-800 mb-2">Per-call overrides</h3>
          <BriefingFields compact value={cfg.briefing} onChange={(b) => setCfg({ ...cfg, briefing: b })} />
        </div>
      )}
    </section>
  );
};
